import React, { useEffect, useState } from 'react'
import { useFormContext, useFieldArray } from 'react-hook-form'
import { useTranslate, required, useRefresh, useNotify, NumberInput, number, minValue, BooleanInput, AutocompleteInput } from 'react-admin'
import { EditInputsWrap } from 'src/components/uiElements/CRUD/EditInputsWrap'
import { Delete, Add } from '@mui/icons-material'
import Button from '@mui/material/Button'
import { Grid, Skeleton } from '@mui/material'
import { JsonInput } from 'src/components/uiElements/react-hook-fields/JsonInputReact'
import { removeProductsByIds } from 'src/components/pages/administration/helper'
import { ConfirmationDialog } from 'src/components/uiElements/styledComponents/ConfirmationDialog'
import { TransferList } from 'src/components/uiElements/react-hook-fields'
import { productServices } from 'src/services/reservation/product/product.service'
import { hasPermissionFunction } from 'src/helper/HasPermission'

export const FieldArrayProduct = ({
  businessUnit,
  productByBusinessUnitList,
  loadingProductByBusinessUnitList,
  editMode = true
}) => {
  const translate = useTranslate()
  const refresh = useRefresh()
  const notify = useNotify()
  const { control } = useFormContext()
  const [openModal, setOpenModal] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [currentIndex, setCurrentIndex] = useState(null)
  const [productList, setProductList] = useState([])
  const [loadingProductList, setLoadingProductList] = useState(false)
  const { fields, append, remove } = useFieldArray({
    control, // control props comes from useForm (optional: if you are using FormContext)
    name: 'product_work_unit_by_business_unit_relation', // unique name for your Field Array
    keyName: '_id',
  })
  const [tableDetails, setTableDetails] = useState(fields.map(field=>field.product_by_work_unit_relation?.product_relation?.ref_table_detail))
  useEffect(() => {
    getProducts()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  

  useEffect(() => {
    if (confirmDelete) {
      removeProductsByIds(currentIndex, fields, remove, editMode, refresh, notify)
      setOpenModal(false)
      setCurrentIndex(null)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [confirmDelete])

  const deleteProduct = (originalIndex, currency, remove, editMode) => {
    if (editMode && !('new_register' in currency)) {
      setOpenModal(true)
      setCurrentIndex(originalIndex)
      return
    }
    remove(originalIndex)
  }

  const getProducts = async () => {
    try {
      setLoadingProductList(true)
      const allProducts = await productServices.getByWorkUnit()
      setProductList(allProducts)
      setLoadingProductList(false)
    } catch (error) {
      setProductList([])
      setLoadingProductList(false)
    }
  }

  return (
    <>
      <ConfirmationDialog
        title={translate('ra.message.delete_product')}
        buttonConfirmText={translate('ra.action.to_confirm')}
        buttonCancel={translate('ra.action.cancel')}
        setOpen={setOpenModal}
        setConfirm={setConfirmDelete}
        open={openModal}
      />
      {
        fields.map((field, index) => (
          <div
            key={field._id}
            style={{
              width: '100%',
              height: 'auto',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <Grid container spacing={3}>
              <EditInputsWrap label={translate('ra.field.product')} required xs={12} md={6} lg={4}>
                {loadingProductList ? (
                  <Skeleton variant='rectangular' width={'100%'} height={40} />
                ) : (
                  <AutocompleteInput
                    source={`product_work_unit_by_business_unit_relation.${index}.product_by_work_unit_relation.id`}
                    optionText='long_name'
                    fullWidth
                    size='small'
                    onChange={(value)=>{
                      let auxTableDetails = [...tableDetails]
                      auxTableDetails[index] = productList.find(product=>product.id === value)?.product_relation?.product_type_relation?.ref_table_detail
                      setTableDetails(auxTableDetails)
                    }}
                    choices={productList}
                    label=''
                    validate={required()}
                  />
                )}
              </EditInputsWrap>
              <EditInputsWrap label={translate('ra.field.days_to_cancel')}>
                <NumberInput
                  source={`product_work_unit_by_business_unit_relation.${index}.days_to_cancel`}
                  label={false}
                  fullWidth
                  validate={[number(), minValue(-300)]}
                />
              </EditInputsWrap>
              <EditInputsWrap label={translate('ra.field.autocancel')}>
                <BooleanInput
                  source={`product_work_unit_by_business_unit_relation.${index}.autocancel`}
                  label={false}
                  fullWidth
                />
              </EditInputsWrap>
              { (tableDetails[index] === 'hotel_detail' &&
                hasPermissionFunction('item','rebooker_view')) &&
                <>
                  <EditInputsWrap label={translate('ra.field.disable_rebooker')} xs={12} md={4} lg={4}>
                    <BooleanInput
                      source={`product_work_unit_by_business_unit_relation.${index}.rebooker_disabled`}
                      label={false}
                      fullWidth
                    />
                  </EditInputsWrap>
                  <EditInputsWrap label={translate('ra.field.days_to_rebook')} xs={12} md={4} lg={4}>
                    <NumberInput
                      source={`product_work_unit_by_business_unit_relation.${index}.rebooker_days`}
                      label={false}
                      fullWidth
                      validate={[number(), minValue(-300)]}
                    />
                  </EditInputsWrap>
                </>
              }
              <EditInputsWrap label={translate('ra.field.json')} xs={12} md={6} lg={6}>
                <JsonInput
                  source={`product_work_unit_by_business_unit_relation.${index}.config`}
                  width='auto'
                />
              </EditInputsWrap>
            </Grid>
            <Grid container spacing={3} justifyContent='flex-end' className='addItemSeparator'>
              <Grid item xs={12} sm={12} className='grid'>
                <Button
                  onClick={() => deleteProduct(index, field, remove, editMode)}
                  startIcon={<Delete />}
                  color='error'
                >
                  {translate('ra.action.delete')}
                </Button>
              </Grid>
            </Grid>
          </div>
        ))
      }
      <Button
        startIcon={<Add />}
        onClick={() =>
          append({
            token: null,
            product_by_work_unit_id: null,
            new_register: true,
          })
        }
      >
        {translate('ra.action.add_specific', {
          name: translate('ra.field.product'),
        })}
      </Button>

      <EditInputsWrap label={translate('ra.field.exclude_product')} md={12}>
        {loadingProductByBusinessUnitList ? (
          <Grid
            container
            spacing={2}
            justifyContent='center'
            alignItems='center'
            className='transferListComponent'
          >
            <Grid item xs={5}>
              <Skeleton variant='rectangular' width={'100%'} height={500} />
            </Grid>
            <Grid item xs={2}>
              <Grid container direction='column' alignItems='center'>
                <Skeleton variant='rectangular' width={'100%'} height={40} />
              </Grid>
            </Grid>
            <Grid item xs={5}>
              <Skeleton variant='rectangular' width={'100%'} height={500} />
            </Grid>
          </Grid>
        ) : (
          <TransferList
            source='excluded_products'
            options={productByBusinessUnitList?.map((elem) => elem.product_relation) || []}
            selected={businessUnit?.excluded_products || []}
            labelField='long_name'
          />
        )}
      </EditInputsWrap>
    </>
  )
}
